import { prefersReducedMotion } from '@theme/utilities';

// Product-card image gallery. The carousel itself is the theme's native
// <slideshow-component> (assets/slideshow.js, CSS scroll-snap) rendered by
// snippets/product-card-image-gallery.liquid — Swiper was removed because its
// per-card container measuring on page load froze Safari on collection pages.
// This element only adds the card hover behavior: advance a slide on
// mouseenter, return to the first slide on mouseleave.
//
// The hover transition is animated here rather than via slideshow.next()/
// select() directly: those scroll with the browser's native `scroll-behavior:
// smooth`, whose duration isn't configurable and — over the short distance of
// one card-width slide — finishes almost instantly. Animating scrollLeft
// ourselves lets us slow the hover preview down without touching the shared
// slideshow component used by carousels elsewhere on the site.
const TRANSITION_MS = 700;

/**
 * @param {number} t
 * @returns {number}
 */
function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

class ProductImageGallery extends HTMLElement {
  /** @type {import('./slideshow').Slideshow | null} */
  _slideshow = null;

  /** @type {Element | null} */
  _card = null;

  /** @type {() => void} */
  _onEnter = () => {};

  /** @type {() => void} */
  _onLeave = () => {};

  /** @type {number} */
  _raf = 0;

  connectedCallback() {
    this._slideshow = this.querySelector('slideshow-component');
    if (!this._slideshow) return;

    this._card = this.closest('.product-card');
    if (this._card) {
      this._onEnter = () => this._goTo(this._slideshow?.nextIndex);
      this._onLeave = () => this._goTo(0);
      this._card.addEventListener('mouseenter', this._onEnter);
      this._card.addEventListener('mouseleave', this._onLeave);
    }
  }

  disconnectedCallback() {
    if (this._card) {
      this._card.removeEventListener('mouseenter', this._onEnter);
      this._card.removeEventListener('mouseleave', this._onLeave);
    }
    cancelAnimationFrame(this._raf);
    this._card = null;
    this._slideshow = null;
  }

  // Scrolls to the slide at `index` over TRANSITION_MS, then syncs the
  // slideshow-component's own state (current index, aria-hidden, dots) with
  // an instant, zero-distance select() now that we're already there.
  // Guarded calls: no-op if slideshow-component hasn't been upgraded yet.
  /**
   * @param {number | undefined} index
   */
  _goTo(index) {
    const slideshow = this._slideshow;
    if (!slideshow || typeof index !== 'number' || typeof slideshow.select !== 'function') return;

    cancelAnimationFrame(this._raf);

    const slides = slideshow.slides;
    if (!slides?.length) return;
    const targetIndex = Math.max(0, Math.min(index, slides.length - 1));
    const target = slides[targetIndex];

    const scroller = slideshow.querySelector('slideshow-slides');
    if (!scroller || !target || prefersReducedMotion()) {
      slideshow.select(targetIndex);
      return;
    }

    const start = scroller.scrollLeft;
    const change = target.offsetLeft - start;
    if (change === 0) {
      slideshow.select(targetIndex, undefined, { animate: false });
      return;
    }

    const startTime = performance.now();
    /** @type {(now: number) => void} */
    const step = (now) => {
      const progress = Math.min((now - startTime) / TRANSITION_MS, 1);
      scroller.scrollLeft = start + change * easeInOutQuad(progress);
      if (progress < 1) {
        this._raf = requestAnimationFrame(step);
      } else {
        slideshow.select(targetIndex, undefined, { animate: false });
      }
    };
    this._raf = requestAnimationFrame(step);
  }
}

if (!customElements.get('product-image-gallery')) {
  customElements.define('product-image-gallery', ProductImageGallery);
}
